import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Container, Button, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import TopNavbar from '../components/TopNavbar';
import Footer from '../components/Footer';
import UpdateProgressBar from '../components/UpdateProgressBar';
import SinglePageNavbar from '../components/SinglePageNavbar';
import { useConfirmModal } from '../hooks/useConfirmModal';
import AppScriptAPI from '../services/appscriptAPI';

function UpdateLayout({
  children,
  currentStep,
  title,
  recordId,
  sheetId,
  onSave,
  onNext,
  onPrevious,
  isSaving = false,
  hasChanges = false,
  canEdit = true,
  showPrevious = true,
  showNext = true,
  showSave = true,
}) {
  const navigate = useNavigate();
  const { isLoading } = useAuth0();
  const { confirm, ConfirmModalJSX } = useConfirmModal();
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isDeceased, setIsDeceased] = useState(false);

  useEffect(() => {
    if (!recordId || !sheetId) return;
    AppScriptAPI.getBasicInfo(recordId, sheetId)
      .then(res => { if (res.success && res.record?.deceased) setIsDeceased(true); })
      .catch(() => {});
  }, [recordId, sheetId]);

  const effectiveCanEdit = canEdit && !isDeceased;

  const handleBackToSearch = async () => {
    if (hasChanges) {
      if (await confirm('You have unsaved changes. Do you want to leave without saving?')) {
        navigate('/search');
      }
    } else {
      navigate('/search');
    }
  };

  const handleNext = () => {
    if (!hasChanges || !canEdit) {
      if (onNext) onNext();
      return;
    }
    setShowUnsavedModal(true);
  };

  const handleUnsavedSave = () => {
    setShowUnsavedModal(false);
    if (onSave) onSave();
  };

  const handleUnsavedLeave = () => {
    setShowUnsavedModal(false);
    if (onNext) onNext();
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '100vh' }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading...</span>
      </div>
    );
  }

  return (
    <div className="single-page-container">
      {ConfirmModalJSX}

      <Modal show={showUnsavedModal} onHide={() => setShowUnsavedModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved changes. Would you like to save them before continuing?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleUnsavedLeave}>
            Leave without saving
          </Button>
          <Button variant="success" onClick={handleUnsavedSave}>
            <i className="bi bi-floppy me-1"></i>Save changes
          </Button>
        </Modal.Footer>
      </Modal>

      <SinglePageNavbar />

      {/* Update Page Header with Pattern */}
      <div className="update-page-header">
        <Container>
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h1 className="update-page-title">{title}</h1>
              <p className="update-page-subtitle">
                Record ID: <strong>{recordId}</strong>
              </p>
            </div>
            {!effectiveCanEdit && (
              <span className="view-only-badge">
                {isDeceased ? 'Deceased — Read Only' : 'View Only'}
              </span>
            )}
          </div>
        </Container>
      </div>

      <main className="update-page-content">
        <Container fluid className="py-4">
          <div className="row justify-content-center">
            <div className="col-12 col-xl-10">
              {/* Progress Bar */}
              <UpdateProgressBar
                currentStep={currentStep}
                recordId={recordId}
                sheetId={sheetId}
              />

              {/* Deceased banner */}
              {isDeceased && (
                <div className="deceased-banner">
                  <i className="bi bi-x-circle-fill"></i>
                  This record has been marked as deceased. No updates can be made.
                </div>
              )}

              {/* Main Content */}
              <div className="update-form-container">{children}</div>

              {/* Action Buttons */}
              <div className="update-actions">
                <div className="row g-3">
                  {/* Top Row: Previous, Save, Next */}
                  <div className="col-12">
                    <div className="row g-2">
                      {showPrevious && currentStep > 1 ? (
                        <>
                          <div className="col-lg-4 col-md-4">
                            <Button
                              variant="secondary"
                              className="w-100"
                              onClick={onPrevious}
                            >
                              <><i className="bi bi-arrow-left me-1"></i>Previous</>
                            </Button>
                          </div>

                          {showSave && effectiveCanEdit && (
                            <div className="col-lg-4 col-md-4">
                              <Button
                                variant="success"
                                className="w-100"
                                onClick={onSave}
                                disabled={isSaving || !hasChanges}
                              >
                                {isSaving ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Saving...
                                  </>
                                ) : (
                                  <><i className="bi bi-floppy me-1"></i>Save Changes</>
                                )}
                              </Button>
                            </div>
                          )}

                          {showNext && (
                            <div className="col-lg-4 col-md-4">
                              <Button
                                variant="primary"
                                className="w-100"
                                onClick={handleNext}
                                style={{
                                  background:
                                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  border: 'none',
                                }}
                              >
                                <>Next<i className="bi bi-arrow-right ms-1"></i></>
                              </Button>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {showSave && effectiveCanEdit && (
                            <div
                              className={`col-lg-${
                                showNext ? '6' : '12'
                              } col-md-${showNext ? '6' : '12'}`}
                            >
                              <Button
                                variant="success"
                                className="w-100"
                                onClick={onSave}
                                disabled={isSaving || !hasChanges}
                              >
                                {isSaving ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Saving...
                                  </>
                                ) : (
                                  <><i className="bi bi-floppy me-1"></i>Save Changes</>
                                )}
                              </Button>
                            </div>
                          )}

                          {showNext && (
                            <div
                              className={`col-lg-${
                                showSave && effectiveCanEdit ? '6' : '12'
                              } col-md-${showSave && effectiveCanEdit ? '6' : '12'}`}
                            >
                              <Button
                                variant="primary"
                                className="w-100"
                                onClick={handleNext}
                                style={{
                                  background:
                                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  border: 'none',
                                }}
                              >
                                <>Next<i className="bi bi-arrow-right ms-1"></i></>
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Bottom Row: Back to Search */}
                  <div className="col-12">
                    <Button
                      variant="outline-secondary"
                      className="w-100"
                      onClick={handleBackToSearch}
                    >
                      <><i className="bi bi-arrow-left me-1"></i>Back to Search</>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}

export default UpdateLayout;
